import React, { forwardRef, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../utils/cn';

export interface ModalProps extends React.HTMLAttributes<HTMLDivElement> {
  isOpen: boolean;
  onClose: () => void;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  children?: React.ReactNode;
}

export interface ModalHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

export interface ModalBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

export interface ModalFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

const focusableSelectors = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
];

const getFocusableElements = (element: HTMLElement | null) =>
  element
    ? Array.from(element.querySelectorAll<HTMLElement>(focusableSelectors.join(','))).filter(
        (focusable) => focusable.offsetParent !== null && !focusable.hasAttribute('disabled'),
      )
    : [];

const mergeRefs = <T,>(...refs: Array<React.Ref<T> | undefined>) => {
  return (value: T | null) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === 'function') {
        ref(value);
      } else {
        (ref as React.MutableRefObject<T | null>).current = value;
      }
    });
  };
};

const Modal = forwardRef<HTMLDivElement, ModalProps>(
  ({ isOpen, onClose, size = 'md', className, children, onKeyDown, ...props }, ref) => {
    const contentRef = useRef<HTMLDivElement | null>(null);
    const previousFocusRef = useRef<HTMLElement | null>(null);

    const handleEscape = useCallback(
      (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          onClose();
        }
      },
      [onClose],
    );

    const setRefs = useCallback(
      mergeRefs<HTMLDivElement>(ref, (node) => {
        contentRef.current = node;
      }),
      [ref],
    );

    useEffect(() => {
      if (isOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'unset';
      }

      return () => {
        document.body.style.overflow = 'unset';
      };
    }, [isOpen]);

    useEffect(() => {
      if (!isOpen) {
        return;
      }

      previousFocusRef.current = document.activeElement as HTMLElement | null;

      const focusable = getFocusableElements(contentRef.current);
      const fallbackTarget = contentRef.current;
      (focusable[0] ?? fallbackTarget)?.focus({ preventScroll: true });

      return () => {
        previousFocusRef.current?.focus({ preventScroll: true });
      };
    }, [isOpen]);

    useEffect(() => {
      if (!isOpen) {
        return;
      }

      document.addEventListener('keydown', handleEscape);

      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }, [isOpen, handleEscape]);

    if (!isOpen) return null;

    const sizeClasses = {
      sm: 'max-w-md',
      md: 'max-w-lg sm:max-w-xl',
      lg: 'max-w-2xl',
      xl: 'max-w-4xl',
      full: 'max-w-full mx-4',
    };

    return createPortal(
      <div
        className="fixed inset-0 z-modal-backdrop bg-black/50 backdrop-blur-sm flex items-center justify-center overflow-y-auto pt-[calc(3.5rem+0.5rem)] pb-[calc(4rem+0.5rem)] lg:pb-[0.5rem] px-3"
        onClick={(event) => {
          if (event.target === event.currentTarget) {
            onClose();
          }
        }}
        role="presentation"
      >
        <div
          ref={setRefs}
          className={cn(
            'bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full flex flex-col my-auto',
            // Mobile: account for TopBar (3.5rem) + MobileBottomNav (4rem) + spacing (1rem)
            // Desktop: account for TopBar (3.5rem) + spacing (1rem)
            'max-h-[calc(100vh-3.5rem-4rem-1rem)] lg:max-h-[calc(100vh-3.5rem-1rem)]',
            sizeClasses[size],
            'animate-in fade-in-0 zoom-in-95 duration-300',
            className,
          )}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
          onKeyDown={(event) => {
            if (event.key === 'Tab') {
              const focusable = getFocusableElements(contentRef.current);
              if (!focusable.length) {
                event.preventDefault();
                contentRef.current?.focus();
                return;
              }

              const currentIndex = focusable.indexOf(document.activeElement as HTMLElement);

              let nextIndex = currentIndex;

              if (event.shiftKey) {
                nextIndex = currentIndex <= 0 ? focusable.length - 1 : currentIndex - 1;
              } else {
                nextIndex = currentIndex === focusable.length - 1 ? 0 : currentIndex + 1;
              }

              focusable[nextIndex]?.focus();
              event.preventDefault();
            }

            onKeyDown?.(event);
          }}
          {...props}
        >
          {children}
        </div>
      </div>,
      document.body,
    );
  },
);

const ModalHeader = forwardRef<HTMLDivElement, ModalHeaderProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'px-4 sm:px-6 pt-4 sm:pt-5 border-b border-neutral-200 bg-white flex-shrink-0 rounded-t-xl sm:rounded-t-2xl',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  },
);

const ModalBody = forwardRef<HTMLDivElement, ModalBodyProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('px-4 sm:px-6 flex-1 min-h-0 overflow-y-auto bg-white', className)} {...props}>
        {children}
      </div>
    );
  },
);

const ModalFooter = forwardRef<HTMLDivElement, ModalFooterProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'px-4 sm:px-6 border-t border-neutral-200 bg-neutral-50 flex items-center justify-end gap-2 sm:gap-3 flex-shrink-0 rounded-b-xl sm:rounded-b-2xl',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  },
);

Modal.displayName = 'Modal';
ModalHeader.displayName = 'ModalHeader';
ModalBody.displayName = 'ModalBody';
ModalFooter.displayName = 'ModalFooter';

export { Modal, ModalHeader, ModalBody, ModalFooter };
